import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, CheckCircle2, Clock, AlertTriangle, XCircle } from "lucide-react";

const STATUS_STYLE = {
  active: {
    badge: "text-emerald-700 bg-emerald-50 border-emerald-200",
    icon: CheckCircle2,
    label: "Active",
  },
  expired: {
    badge: "text-red-700 bg-red-50 border-red-200",
    icon: AlertTriangle,
    label: "Expired",
  },
  canceled: {
    badge: "text-gray-700 bg-gray-100 border-gray-200",
    icon: XCircle,
    label: "Canceled",
  },
  never: {
    badge: "text-amber-800 bg-amber-50 border-amber-200",
    icon: Clock,
    label: "Not a member yet",
  },
};

function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

export default function MembershipCard() {
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/contractor/membership");
      setMembership(r.data);
    } catch {
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Post-Stripe redirect: poll until paid
  const sessionId = searchParams.get("membership_session");
  const canceled = searchParams.get("membership_canceled");
  useEffect(() => {
    if (canceled) {
      toast.info("Membership checkout canceled");
      searchParams.delete("membership_canceled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    let cancel = false;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/contractor/membership/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          toast.success("Membership renewed — you're active for 30 days.");
          searchParams.delete("membership_session");
          setSearchParams(searchParams, { replace: true });
          await load();
          return;
        }
        if (!cancel && attempts < 6) setTimeout(poll, 2000);
        else if (!cancel) {
          toast.info("Renewal still processing — refresh in a moment.");
          searchParams.delete("membership_session");
          setSearchParams(searchParams, { replace: true });
        }
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || "Status check failed");
      }
    };
    poll();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, canceled]);

  const onRenew = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/contractor/membership/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!window.confirm("Cancel membership? You'll stay active until the current period ends.")) return;
    try {
      await api.post("/contractor/membership/cancel", {});
      toast.success("Membership canceled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-6">Loading membership…</CardContent>
      </Card>
    );
  }
  if (!membership) return null;

  const style = STATUS_STYLE[membership.status] || STATUS_STYLE.never;
  const Icon = style.icon;
  const needsRenewal =
    membership.status === "never" ||
    membership.status === "expired" ||
    (typeof membership.days_left === "number" && membership.days_left <= 7);

  return (
    <Card className="border-[#E5E7EB]" data-testid="membership-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-['Outfit'] flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#C25934]" />
          ClaimScope Pro Membership
        </CardTitle>
        <span
          className={`text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${style.badge}`}
          data-testid="membership-status-badge"
        >
          <Icon className="w-3 h-3" />
          {style.label}
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-500">Rate</p>
            <p className="font-semibold text-[#1E3F33]">
              {fmtUSD(membership.amount_usd)}{" "}
              <span className="text-xs text-gray-500 font-normal">/ {membership.interval_days}d</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Days left</p>
            <p
              className={`font-semibold ${
                typeof membership.days_left === "number" && membership.days_left <= 7
                  ? "text-[#C25934]"
                  : "text-[#1E3F33]"
              }`}
              data-testid="membership-days-left"
            >
              {typeof membership.days_left === "number" ? membership.days_left : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Last payment</p>
            <p className="font-semibold text-[#1E3F33] font-mono">
              {membership.last_payment_at ? membership.last_payment_at.slice(0, 10) : "—"}
            </p>
          </div>
          {membership.current_period_end && (
            <div>
              <p className="text-xs text-gray-500">Period ends</p>
              <p className="font-semibold text-[#1E3F33] font-mono">
                {membership.current_period_end.slice(0, 10)}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={onRenew}
            disabled={submitting}
            className="bg-[#C25934] hover:bg-[#a94a2b] text-white"
            data-testid="membership-renew-btn"
          >
            <Crown className="w-4 h-4" />
            {submitting
              ? "Redirecting…"
              : needsRenewal
                ? `Activate for ${fmtUSD(membership.amount_usd)}`
                : "Renew now"}
          </Button>
          {membership.status === "active" && (
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-gray-600 hover:text-red-600 underline-offset-2 hover:underline"
              data-testid="membership-cancel-btn"
            >
              Cancel at period end
            </button>
          )}
        </div>

        {needsRenewal && membership.status !== "canceled" && (
          <div className="text-xs text-[#C25934] bg-[#FEF3EC] border border-[#F3D1BF] rounded-md p-3">
            {membership.status === "expired"
              ? "Your membership has lapsed. Renew to appear in contractor search and receive claim referrals."
              : membership.status === "never"
                ? "Activate Pro to appear in the contractor directory and receive zip-matched claim referrals."
                : `Your membership expires in ${membership.days_left} day${
                    membership.days_left === 1 ? "" : "s"
                  }. Renew now to keep your listing live.`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
